import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import AOS from "aos";
import "aos/dist/aos.css";
import { NavLink } from "react-router-dom";
import SmartOrderPromo from "../../components/SmartOrderPromo/SmartOrderPromo";
import "./QrMenuPage.css";
import { useTranslation } from "react-i18next";
import { Smartphone, Eye, Settings } from "lucide-react";

export default function QrMenuPage() {
  const { t } = useTranslation();
  const phoneNumber = "+37443099070";
  const [openFaq, setOpenFaq] = useState(null);

  const whatsappLink = `https://wa.me/${phoneNumber}?text=Hello,%20I%20am%20interested%20in%20the%20QR%20Menu%20system`;
  const demoUrl = "https://order.elyx.work";

  const heroPills = t("qrMenu.heroPills", { returnObjects: true }) || [];
  const features = t("qrMenu.features", { returnObjects: true }) || [];
  const steps = t("qrMenu.steps", { returnObjects: true }) || [];
  const plans = t("qrMenu.plans", { returnObjects: true }) || [];
  const examples = t("qrMenu.examples", { returnObjects: true }) || [];
  const faqs = t("qrMenu.faqs", { returnObjects: true }) || [];

  const iconMap = {
    smartphone: <Smartphone size={48} strokeWidth={1.5} />,
    eye: <Eye size={48} strokeWidth={1.5} />,
    settings: <Settings size={48} strokeWidth={1.5} />,
  };

  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  return (
    <div className="qrmenu-page" id="top">
      <style>{`
        .example-card:hover .qr-logo {
          transform: translate(-50%, -50%) rotate(360deg) !important;
        }
      `}</style>
      <Helmet>
        <title>{t("qrMenu.metaTitle")}</title>
        <meta name="description" content={t("qrMenu.metaDescription")} />
      </Helmet>

      <section className="qr-hero" data-aos="fade-up">
        <div className="qr-hero__copy">
          <p className="eyebrow">{t("qrMenu.heroEyebrow")}</p>
          <h1>{t("qrMenu.heroTitle")}</h1>
          <p className="lede">{t("qrMenu.heroLede")}</p>
          <div className="hero-actions">
            <a
              href={demoUrl}
              className="btn primary"
              target="_blank"
              rel="noreferrer noopener"
            >
              {t("qrMenu.heroPrimaryCta")}
            </a>
            <NavLink to="/get-in-touch" className="btn ghost">
              {t("free_consultation")}
            </NavLink>
          </div>
          <div className="hero-pills">
            {Array.isArray(heroPills) &&
              heroPills.map((pill) => <span key={pill}>{pill}</span>)}
          </div>
        </div>

        <div className="qr-hero__device" data-aos="zoom-in">
          <div className="device-frame">
            <img src="/images/qr-code.svg" alt="QR code preview" />
            <div className="scan-line" aria-hidden="true"></div>
          </div>
          <p className="device-caption">{t("qrMenu.heroDeviceCaption")}</p>
        </div>
      </section>

      <section className="examples" data-aos="fade-up">
        <div className="section-header">
          <p className="eyebrow">{t("qrMenu.examplesHeaderEyebrow")}</p>
          <h2>{t("qrMenu.examplesHeaderTitle")}</h2>
          <p className="lede">{t("qrMenu.examplesHeaderLede")}</p>
        </div>
        <div className="example-grid">
          {Array.isArray(examples) &&
            examples.map((item) => (
              <div className="card example-card" key={item.name}>
                <div
                  style={{
                    position: "relative",
                    width: "120px",
                    height: "120px",
                    marginBottom: "1rem",
                    display: "inline-block",
                  }}
                >
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&ecc=H&data=${encodeURIComponent(
                      item.url,
                    )}`}
                    alt={`QR for ${item.name}`}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                    }}
                  />
                  <img
                    src="/Logo.png"
                    alt="Logo"
                    className="qr-logo"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      transition: "transform 0.6s ease-in-out",
                      width: "32px",
                      height: "32px",
                      backgroundColor: "#fff",
                      padding: "4px",
                      borderRadius: "6px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  />
                </div>
                <h3>{item.name}</h3>
                <p>{item.desc}</p>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn ghost"
                >
                  {t("qrMenu.visitSite")}
                </a>
              </div>
            ))}
        </div>
      </section>

      <section id="smart-order-promo" data-aos="fade-up">
        <SmartOrderPromo />
      </section>

      <section className="feature-grid" data-aos="fade-up">
        <div className="section-header">
          <p className="eyebrow">{t("qrMenu.featuresEyebrow")}</p>
          <h2>{t("qrMenu.featuresTitle")}</h2>
          <p className="lede">{t("qrMenu.featuresSubtitle")}</p>
        </div>
        <div className="grid">
          {Array.isArray(features) &&
            features.map((item) => (
              <div className="card feature-card" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
        </div>
      </section>

      <section className="workflow" data-aos="fade-up">
        <div className="section-header">
          <p className="eyebrow">{t("qrMenu.workflowEyebrow")}</p>
          <h2>{t("qrMenu.workflowTitle")}</h2>
        </div>
        <div className="steps">
          {Array.isArray(steps) &&
            steps.map((step, idx) => (
              <div className="card step" key={step.title}>
                <div className="step-badge">0{idx + 1}</div>
                <div className="step-icon" aria-hidden="true">
                  {iconMap[step.icon]}
                </div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            ))}
        </div>
      </section>

      <section className="cta-panel" data-aos="fade-up">
        <div className="cta-panel">
          <p className="eyebrow">{t("qrMenu.ctaEyebrow")}</p>
          <h2>{t("qrMenu.ctaTitle")}</h2>
          <p className="lede">{t("qrMenu.ctaLede")}</p>
          <div className="hero-actions">
            <a
              href={demoUrl}
              className="btn primary"
              target="_blank"
              rel="noreferrer noopener"
            >
              {t("qrMenu.heroPrimaryCta")}
            </a>
            <NavLink to="/get-in-touch" className="btn ghost">
              {t("free_consultation")}
            </NavLink>
          </div>
        </div>
        {/* <div className="cta-list">
          <div className="card-mini">
            <h4>Մենյուի կառավարում</h4>
            <ul>
              {menuControls.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="card-mini">
            <h4>Բրենդավորում</h4>
            <ul>
              {branding.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div> */}
      </section>

      <section className="pricing" data-aos="fade-up">
        <div className="section-header">
          <p className="eyebrow">{t("qrMenu.pricingEyebrow")}</p>
          <h2>{t("qrMenu.pricingTitle")}</h2>
          <p className="lede">{t("qrMenu.pricingSubtitle")}</p>
        </div>
        <div className="plans">
          {Array.isArray(plans) &&
            plans.map((plan) => (
              <div
                className={`plan card ${plan.badge ? "highlight" : ""}`}
                key={plan.name}
              >
                {plan.badge && <span className="badge">{plan.badge}</span>}
                <h3>{plan.name}</h3>
                <p className="plan-blurb">{plan.blurb}</p>
                {/* <ul>
                {plan.perks.map((perk) => (
                  <li key={perk}>{perk}</li>
                ))}
              </ul> */}
                <strong>{plan.price}</strong>
                <a
                  href={plan.link}
                  className="btn primary"
                  target="_blank"
                  rel="noreferrer"
                >
                  {plan.cta}
                </a>
              </div>
            ))}
        </div>
      </section>

      <section className="faq" data-aos="fade-up">
        <div className="section-header">
          <p className="eyebrow">{t("qrMenu.faqEyebrow")}</p>
          <h2>{t("qrMenu.faqTitle")}</h2>
        </div>
        <div className="faq-list">
          {Array.isArray(faqs) &&
            faqs.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  className={`faq-item ${isOpen ? "open" : ""}`}
                  key={item.q}
                >
                  <button
                    className="faq-question"
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                  >
                    {item.q}
                    <span className="faq-toggle">{isOpen ? "-" : "+"}</span>
                  </button>
                  {isOpen && (
                    <div className="faq-answer">
                      <p>{item.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </section>

      <section className="contact-now" data-aos="fade-up">
        <div className="section-header">
          <p className="eyebrow">{t("qrMenu.contactEyebrow")}</p>
          <h2>{t("qrMenu.contactTitle")}</h2>
        </div>
        <div className="contact-actions">
          <a href={`tel:${phoneNumber}`} className="btn primary">
            {t("qrMenu.contactPrimary")}
          </a>
          <a
            href={whatsappLink}
            className="btn ghost"
            target="_blank"
            rel="noreferrer"
          >
            {t("qrMenu.contactSecondary")}
          </a>
        </div>
      </section>
    </div>
  );
}
